import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCamera } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';
import { enveloppeDe } from '../../components/Utils/enveloppeDe';
import { estPhotoPosee, typeImage } from '../../components/Utils/typeImage';
import { reduirePhoto } from '../../components/Utils/reduirePhoto';
import AvatarCircle from '../../components/Utils/AvatarCircle';
import Modal from '../../components/Utils/ModalShow';
import EditionCompetences, { memeContenu, versBrouillon, versCorps } from './EditionCompetences';
import PastillesCompetences from './PastillesCompetences';

/**
 * Modale « Modifier mon profil » (YRH-NEW-008, Yros/Kavero#479, #481) : photo,
 * bio, compétences et langues dans un seul brouillon, un seul Annuler et un
 * seul Enregistrer. Rien n'est envoyé
 * avant « Enregistrer », et l'envoi est un seul POST /api/rh/mon-profil qui ne
 * porte que les rubriques changées — le serveur les applique en tout ou rien.
 *
 * Les limites (bio, photo) sont rejouées ici pour éviter un aller-retour
 * inutile, mais c'est le serveur qui juge : son refus s'affiche mot pour mot et
 * la modale reste ouverte avec le brouillon.
 */

export const BIO_MAX = 500;
const PHOTO_MAX = 2 * 1024 * 1024;
const TYPES_PHOTO = ['image/png', 'image/jpeg'];

function ModifierMonProfil({ profil, nom, catalogue, onClose, onEnregistre }) {
  const { t } = useTranslation();
  const [bio, setBio] = useState(profil.bio || '');
  // La photo choisie, pas encore enregistrée : { base64, url }.
  const [apercu, setApercu] = useState(null);
  const [retrait, setRetrait] = useState(false);
  const [competences, setCompetences] = useState(() => versBrouillon(profil.competences));
  const [langues, setLangues] = useState(() => versBrouillon(profil.langues));
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const photoPosee = estPhotoPosee(profil.photo);
  const bioChangee = bio !== (profil.bio || '');
  // Comparées par leur contenu : lire de nouveau le profil crée de nouveaux
  // tableaux, ce n'est pas un changement.
  const competencesChangees = !memeContenu(competences, versBrouillon(profil.competences));
  const languesChangees = !memeContenu(langues, versBrouillon(profil.langues));
  const modifie = bioChangee || Boolean(apercu) || retrait || competencesChangees || languesChangees;

  const choisirPhoto = (evenement) => {
    const champ = evenement.target;
    const fichier = champ.files && champ.files[0];
    if (!fichier) return;
    setErreur(null);
    if (!TYPES_PHOTO.includes(fichier.type)) {
      setErreur(t('monProfil.photoRefusee'));
      champ.value = '';
      return;
    }
    champ.value = '';
    if (fichier.size > PHOTO_MAX) {
      // Yros/Kavero#488 : trop lourde, elle est réduite plutôt que refusée.
      reduirePhoto(fichier)
        .then((url) => {
          const base64 = url.split(',')[1];
          // Le base64 pèse 4/3 des octets qu'il porte.
          if (base64.length * 3 / 4 > PHOTO_MAX) throw new Error('Photo encore trop lourde');
          setApercu({ base64, url });
          setRetrait(false);
        })
        .catch(() => setErreur(t('monProfil.photoIrreductible')));
      return;
    }
    const lecteur = new FileReader();
    lecteur.onload = () => {
      // `data:image/png;base64,` n'est pas du base64 : le serveur décode la
      // chaîne telle quelle.
      const url = String(lecteur.result);
      setApercu({ base64: url.split(',')[1], url });
      setRetrait(false);
    };
    lecteur.readAsDataURL(fichier);
  };

  const enregistrer = async () => {
    if (enCours || !modifie) return;
    const rubriques = {};
    if (bioChangee) rubriques.bio = bio;
    // Une chaîne vide retire la photo : c'est le contrat du serveur.
    if (apercu) rubriques.photo = apercu.base64;
    else if (retrait) rubriques.photo = '';
    if (competencesChangees) rubriques.competences = versCorps(competences);
    if (languesChangees) rubriques.langues = versCorps(langues);

    setEnCours(true);
    setErreur(null);
    const reponse = await enveloppeDe(() => apiService.ecrireMonProfil(rubriques));
    setEnCours(false);
    if (!reponse.ok) {
      setErreur(reponse.message || t('monProfil.erreurGenerique'));
      return;
    }
    notify(t('monProfil.enregistre'), '', 2);
    await onEnregistre();
    onClose();
  };

  let vignette;
  if (apercu) {
    vignette = (
      <img
        data-testid="profil-photo-apercu"
        src={apercu.url}
        alt={t('monProfil.apercu')}
        className="w-16 h-16 rounded-full object-cover border-2 border-[--color-primary]"
      />
    );
  } else if (photoPosee && !retrait) {
    vignette = (
      <img
        data-testid="profil-modale-photo"
        src={`data:${typeImage(profil.photo)};base64,${profil.photo}`}
        alt={t('monProfil.photo')}
        className="w-16 h-16 rounded-full object-cover border border-slate-200"
      />
    );
  } else {
    vignette = (
      <span data-testid="profil-modale-avatar">
        <AvatarCircle name={nom} size={64} />
      </span>
    );
  }

  const pied = (
    <>
      <button
        type="button"
        data-testid="profil-annuler"
        disabled={enCours}
        onClick={onClose}
        className="px-4 py-2 text-sm rounded-lg text-[--color-primary] hover:bg-gray-100 disabled:opacity-50 transition-colors duration-200"
      >
        {t('monProfil.annuler')}
      </button>
      <button
        type="button"
        data-testid="profil-enregistrer"
        disabled={enCours || !modifie}
        onClick={enregistrer}
        className="px-4 py-2 text-sm bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
      >
        {t('monProfil.enregistrer')}
      </button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title={t('monProfil.modifierTitre')} size={560} footer={pied}>
      <div data-testid="modale-profil" className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
        {erreur && (
          <p
            data-testid="profil-erreur"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {erreur}
          </p>
        )}

        <div className="flex items-center gap-4">
          {vignette}
          <div className="flex flex-col items-start gap-1.5">
            <label className="inline-flex items-center gap-2 text-sm text-[--color-primary] cursor-pointer hover:underline">
              <FaCamera className="w-4 h-4" />
              {t('monProfil.changerPhoto')}
              <input
                type="file"
                accept="image/png,image/jpeg"
                data-testid="profil-photo-champ"
                className="hidden"
                onChange={choisirPhoto}
                disabled={enCours}
              />
            </label>
            {/* Rien à retirer sans photo : l'avatar SVG par défaut d'Odoo n'est
                pas une photo posée (seuls PNG et JPEG se téléversent). Pendant
                l'aperçu d'une nouvelle photo, « Annuler » suffit. */}
            {photoPosee && !retrait && !apercu && (
              <button
                type="button"
                data-testid="profil-photo-retirer"
                disabled={enCours}
                onClick={() => setRetrait(true)}
                className="text-sm text-red-600 hover:underline disabled:opacity-50"
              >
                {t('monProfil.retirerPhoto')}
              </button>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="profil-bio" className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
            {t('monProfil.bio')}
          </label>
          <textarea
            id="profil-bio"
            data-testid="profil-bio"
            value={bio}
            maxLength={BIO_MAX}
            rows={4}
            onChange={(e) => setBio(e.target.value)}
            className="w-full text-sm rounded-lg border border-slate-200 p-2.5 focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
          />
          <span className="block mt-1 text-xs text-slate-400 text-right">{bio.length}/{BIO_MAX}</span>
        </div>

        {catalogue ? (
          <>
            <EditionCompetences
              prefixe="competences"
              titre={t('monProfil.competences')}
              libelleAjout={t('monProfil.ajouterCompetence')}
              lignes={competences}
              catalogue={catalogue.competences}
              libre
              enCours={enCours}
              onChange={setCompetences}
            />
            <EditionCompetences
              prefixe="langues"
              titre={t('monProfil.langues')}
              libelleAjout={t('monProfil.ajouterLangue')}
              lignes={langues}
              catalogue={catalogue.langues}
              libre={false}
              enCours={enCours}
              onChange={setLangues}
            />
          </>
        ) : (
          <>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                {t('monProfil.competences')}
              </p>
              <PastillesCompetences lignes={profil.competences} testid="modale-competences" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                {t('monProfil.langues')}
              </p>
              <PastillesCompetences lignes={profil.langues} testid="modale-langues" />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default ModifierMonProfil;
